import React, { useState } from 'react';
import type {
  Calendar as CalendarType,
  Candidate,
  CandidateEngagementDetails,
} from '@axiom/validation';
import { useApi, useBreakpoint } from '@axiom/ui';

import { CandidateOpportunitiesLegacyApi } from '../../api/candidate-opportunities-legacy-api';
import {
  InterviewAvailabilityUtil,
  InterviewAvailabilityConst,
} from '../../utils/interview-availability-util';
import { WarmingModal } from '../WarmingModal/WarmingModal';
import { InterviewAvailabilityShareable } from '../InterviewAvailabilityShareable/InterviewAvailabilityShareable';
import { CalendarApi } from '../../api/calendar-api';
import { CandidateEngagementsApi } from '../../api/candidate-engagements-api';

const { minimumHoursRequired } = InterviewAvailabilityConst;
const { calculateAvailabilityWithinNextTwoWeeks } = InterviewAvailabilityUtil;

export type WarmingFlowProps = {
  candidate: Candidate;
  engagement: CandidateEngagementDetails;
  name?: string;
  onClose: () => void;
};

export const WarmingFlow = ({
  candidate,
  engagement,
  name = 'WARMING_FLOW',
  onClose,
}: WarmingFlowProps) => {
  const { isMobile } = useBreakpoint();
  const [isWarmingModalOpen, setIsWarmingModalOpen] = useState(true);
  const [isInterviewModalOpen, setIsInterviewModalOpen] = useState(false);

  const { id: calendarId = null } = candidate.calendar || {};

  const [
    { data: calendarData } = {
      data: {
        events: null,
      } as Partial<CalendarType>,
    },
  ] = useApi(calendarId ? CalendarApi.readCalendar(calendarId) : undefined);

  const shouldShowInterviewAvailabilityModal =
    calculateAvailabilityWithinNextTwoWeeks(calendarData.events ?? []) <
      minimumHoursRequired && !isMobile;

  const handleClose = async () => {
    await Promise.all([
      CandidateOpportunitiesLegacyApi.refreshOpportunitiesByCandidateId(
        engagement.candidate.id
      ),
      CandidateEngagementsApi.refreshEngagementsCount(engagement.candidate.id),
      CandidateEngagementsApi.refreshEngagementsCurrent(
        engagement.candidate.id
      ),
      CandidateEngagementsApi.refreshEngagementsCurrentDetails(
        engagement.candidate.id,
        engagement.opportunity.id
      ),
      CandidateEngagementsApi.refreshEngagementsHistory(
        engagement.candidate.id
      ),
    ]);
    onClose();
  };

  return (
    <div data-test={name}>
      {isWarmingModalOpen ? (
        <WarmingModal
          name="WARMING_MODAL"
          candidate={candidate}
          engagement={engagement}
          onClose={onClose}
          onAfterSubmit={async () => {
            if (shouldShowInterviewAvailabilityModal) {
              setIsWarmingModalOpen(false);
              setIsInterviewModalOpen(true);
            } else {
              await handleClose();
            }
          }}
          submitButtonText={
            shouldShowInterviewAvailabilityModal ? 'Next' : 'Submit'
          }
        />
      ) : null}
      {isInterviewModalOpen ? (
        <InterviewAvailabilityShareable
          name="INTERVIEW_AVAILABILITY_SHAREABLE"
          candidateId={candidate.id}
          title="Complete next steps for this opportunity"
          onAfterSubmit={async () => {
            await handleClose();
          }}
          onClose={async () => {
            await handleClose();
          }}
        />
      ) : null}
    </div>
  );
};
